"use client";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Download, Filter, RefreshCw, Search, ArrowUp, ArrowDown, ArrowDownUp, X, MoreHorizontal, Globe2, ListFilter } from "lucide-react";
import { formatDateInZone, formatCurrency, getCurrencySymbol } from "../../utils/formatters";
import { SalesTableProps } from "./SalesInterface";
import { PaidState } from "../../types/order/paidState";
import { LogisticState } from "../../types/order/logisticState";
import {
  downloadOrders,
  downloadOrdersXlsx,
  getOrdersByUser,
  getOrderDetails,
  deleteOrder,
  confirmDropiAfterWhatsApp,
  retryFailedShopifyOrders,
  retryDropiOrder,
  retryMastershopOrder,
  retryShopifyOrder,
} from "../../services/orders/orderAxios";
import { dictionary, Language } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { DropiState, MastershopState, PaymentProvider, ShopifyState } from "../../pages/SalesPage/SalesPageInterface";
import { BaseModal } from "../modals/BaseModal";
import { getErrorMessage, handleApi } from "../../utils/handleApi";
import { Order } from "../../pages/SalesPage/SalesPageInterface";
import { toast } from "react-hot-toast";
import { DateFilter } from "./DateFilter";
import { ProductFilter } from "./ProductFilter";
import { FunnelFilter } from "./FunnelFilter";
import { useSearchParams } from "react-router-dom";
import {
  getConfirmationStatuses,
  type ConfirmationInfo,
  type ConfirmationStatus,
} from "../../services/whatsapp/whatsappService";
import {
  getOrderDisplayCurrencyCode,
  getOrderDisplayTotalWithDiscount,
} from "../../utils/orderDisplayTotals";
import { decodeTokenService } from "../../services/decodeTokenService";
import { OrderFunnelLink } from "./OrderFunnelLink";
import { getCountriesList } from "../../const/countryList";
import { OrderDetailDrawer } from "./OrderDetailDrawer";

interface DeleteOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  onConfirm: () => void;
  loading: boolean;
  language: Language;
}

const DeleteOrderModal = ({
  isOpen,
  onClose,
  message,
  onConfirm,
  loading,
  language,
}: DeleteOrderModalProps) => (
  <BaseModal
    isOpen={isOpen}
    onClose={onClose}
    imageSrc="https://fluxi.s3.amazonaws.com/prod/assets/media/warning-purple.gif"
    imageAlt="Warning"
    title={dictionary["delete-order"][language]}
    description={message}
    buttonOne={{
      text: dictionary["delete"][language],
      onClick: onConfirm,
      loading,
      disabled: loading,
    }}
    buttonTwo={{
      text: dictionary["cancel"][language],
      onClick: onClose,
    }}
  />
);

const EmptyState = ({ language }: { language: Language }) => (
  <tr>
    <td colSpan={9} className="px-6 py-12 text-center">
      <div className="flex flex-col items-center justify-center">
        <svg
          className="w-16 h-16 text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {dictionary["no-orders-found"]?.[language] ||
            "No se encontraron órdenes"}
        </h3>
        <p className="text-sm text-gray-500">
          {dictionary["try-different-filters"]?.[language] ||
            "Intenta con diferentes filtros o rango de fechas"}
        </p>
      </div>
    </td>
  </tr>
);

interface OrderRowProps {
  order: Order;
  fallbackCurrencySymbol: string;
  language: Language;
  timezone: string;
  paymentProviderLabels: Record<PaymentProvider, string>;
  onOpenDetails: (order: Order) => void;
  onRefreshOrders?: () => void;
  confirmationStatus?: ConfirmationStatus;
  compact?: boolean;
  selected?: boolean;
}

const IntegrationErrorIcon = ({ title, description }: { title: string; description?: string }) => (
  <div className="relative group flex justify-center">
    <div className="bg-yellow-500 rounded-lg p-2 cursor-help">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="lucide lucide-alert-triangle text-white"
      >
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
        <path d="M12 9v4"></path>
        <path d="M12 17h.01"></path>
      </svg>
    </div>
    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
      <p className="font-medium mb-1">{title}</p>
      {description && <p className="text-gray-300">{description}</p>}
    </div>
  </div>
);

const DropiConfirmationRetry = ({
  loading,
  onRetry,
}: {
  loading: boolean;
  onRetry: () => void;
}) => (
  <div className="relative group flex justify-center">
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onRetry();
      }}
      disabled={loading}
      className="bg-emerald-600 rounded-lg p-2 text-white disabled:opacity-60 disabled:cursor-not-allowed hover:bg-emerald-700 transition-colors"
      aria-label="Reintentar confirmación en Dropi"
    >
      {loading ? (
        <span className="block h-[18px] w-[18px] rounded-full border-2 border-white/40 border-t-white animate-spin" />
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-white"
        >
          <path d="M21 12a9 9 0 1 1-3-6.7" />
          <path d="M21 3v6h-6" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      )}
    </button>
    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
      <p className="font-medium mb-1">Confirmado en Fluxi, pendiente en Dropi</p>
      <p className="text-gray-300">Haz click para reenviar la confirmación manualmente a Dropi.</p>
    </div>
  </div>
);

const ShopifyManualRetryButton = ({
  loading,
  onRetry,
}: {
  loading: boolean;
  onRetry: () => void;
}) => (
  <div className="relative group flex justify-center">
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onRetry();
      }}
      disabled={loading}
      className="rounded-lg bg-blue-500 p-2 text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
      aria-label="Sincronizar con Shopify"
    >
      {loading ? (
        <span className="block h-[18px] w-[18px] rounded-full border-2 border-white/40 border-t-white animate-spin" />
      ) : (
        <RefreshCw className="h-[18px] w-[18px]" />
      )}
    </button>
    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
      <p className="font-medium mb-1">Sincronizar con Shopify</p>
      <p className="text-gray-300">
        Reintenta enviar esta orden a Shopify.
      </p>
    </div>
  </div>
);

const MastershopManualRetryButton = ({
  loading,
  onRetry,
  language,
}: {
  loading: boolean;
  onRetry: () => void;
  language: Language;
}) => (
  <div className="relative group flex justify-center">
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onRetry();
      }}
      disabled={loading}
      className="rounded-lg bg-blue-500 p-2 text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
      aria-label={dictionary["mastershop-sync-tooltip-title"][language]}
    >
      {loading ? (
        <span className="block h-[18px] w-[18px] rounded-full border-2 border-white/40 border-t-white animate-spin" />
      ) : (
        <RefreshCw className="h-[18px] w-[18px]" />
      )}
    </button>
    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
      <p className="font-medium mb-1">{dictionary["mastershop-sync-tooltip-title"][language]}</p>
      <p className="text-gray-300">
        {dictionary["mastershop-sync-tooltip-description"][language]}
      </p>
    </div>
  </div>
);

const normalizeDropiStatus = (status?: string | null) =>
  (status || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ");

const isPendingDropiConfirmation = (status?: string | null) => {
  const normalized = normalizeDropiStatus(status);
  return (
    normalized.includes("PENDIENTE") &&
    (normalized.includes("CONFIRMACION") || normalized.includes("CONFIRMAR"))
  );
};

const isShopifyRetryable = (order: Order) =>
  Boolean(order.shopify_integration_error) ||
  order.shopify_integration_state === ShopifyState.ERROR ||
  order.shopify_integration_state === ShopifyState.PENDING_SYNC;

const normalizeOrderState = (value?: string | null) =>
  (value || "").trim().toLowerCase().replace(/[_-]/g, " ");

const isCanceledOrder = (order: Order) => {
  const state = normalizeOrderState(order.state || order.order_state);
  return state === "canceled" || state === "cancelled" || state === "cancelado";
};

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "all", label: "Todos los estados" },
  { value: "paid", label: "Pago" },
  { value: "pending", label: "Por recaudar" },
  { value: "unfulfilled", label: "Envio pendiente" },
  { value: "fulfilled", label: "En gestion" },
  { value: "dropi_success", label: "Sincronizada Dropi" },
  { value: "dropi_pending_sync", label: "Pendiente sincronizar Dropi" },
  { value: "dropi_error", label: "Sync fallida Dropi" },
  { value: "canceled", label: "Cancelada" },
  { value: "chatbot", label: "Chatbot" },
];

const neutralChipClass =
  "inline-flex max-w-full min-w-0 items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700";

const NeutralChip = ({ children, title }: { children: React.ReactNode; title?: string }) => (
  <span className={neutralChipClass} title={title}>
    <span className="min-w-0 truncate">{children}</span>
  </span>
);

const getStatusFilterLabel = (value: string) =>
  STATUS_FILTER_OPTIONS.find((option) => option.value === value)?.label || value;

const HeaderText = ({ children }: { children: React.ReactNode }) => (
  <span className="block min-w-0 max-w-full truncate">{children}</span>
);

const getOrderDateTimeParts = (date: Order["created_at"], timezone: string) => {
  const full = formatDateInZone(date, timezone);
  return {
    full,
    date: formatDateInZone(date, timezone, "dd/MM/yyyy"),
    time: formatDateInZone(date, timezone, "HH:mm"),
  };
};

const getOrderProductSummary = (order: Order) => {
  const summary = String(order.product_summary || "").trim();
  if (summary) {
    const count = Number(order.products_count || 0);
    return count > 1 ? `${summary} +${count - 1}` : summary;
  }
  return order.total_units > 1 ? `${order.total_units} unidades` : "Producto";
};

const getOrderChannelLabel = (order: Order) => {
  const channel = String(order.channel || "").trim().toLowerCase();
  const source = String(order.tracking_source || "").trim().toLowerCase();
  if (channel === "chatbot" || source === "chatbot_valeria") return "Chatbot";
  return "Embudo";
};

const getDropiObservedLabel = (status?: string | null) => {
  const normalized = normalizeDropiStatus(status);
  if (!normalized) return null;
  if (normalized.includes("GUIA") || normalized.includes("GUIA GENERADA")) return "Guia generada Dropi";
  if (normalized.includes("ENTREG")) return "Entregada Dropi";
  if (normalized.includes("OFICINA") || normalized.includes("PUNTO")) return "En oficina/punto";
  if (normalized.includes("RUTA") || normalized.includes("CAMINO") || normalized.includes("TRANSPORTE")) return "En camino";
  if (normalized.includes("CONFIRM")) return "Confirmada Dropi";
  if (normalized.includes("PENDIENTE")) return "Pendiente Dropi";
  return status || null;
};

const getOrderLogisticsDisplay = (order: Order, observedStatus?: string | null) => {
  if (order.logistics_status_label) {
    return {
      label: order.logistics_status_label,
      retryableDropi: order.logistics_status_code === "dropi_error" || order.dropi_state === DropiState.ERROR,
      raw:
        order.dropi_logistics?.last_error ||
        order.dropi_logistics?.dropi_observed_status_normalized ||
        observedStatus ||
        order.dropi_observed_status_normalized ||
        order.dropi_observed_status ||
        null,
    };
  }
  if (order.order_state === "draft") {
    return { label: "Borrador", retryableDropi: false, raw: null };
  }
  if (isCanceledOrder(order)) {
    return { label: "Cancelada", retryableDropi: false, raw: order.logistic_state };
  }
  if (order.dropi_state === DropiState.ERROR) {
    return { label: "Sync fallida Dropi", retryableDropi: true, raw: order.dropi_integration_error || observedStatus };
  }
  if (order.dropi_state === DropiState.PENDING_SYNC) {
    return { label: "Pendiente sincronizar Dropi", retryableDropi: false, raw: observedStatus };
  }
  const observedLabel = getDropiObservedLabel(observedStatus || order.dropi_order_status || order.dropi_observed_status);
  if (observedLabel) {
    return { label: observedLabel, retryableDropi: false, raw: observedStatus || order.dropi_observed_status };
  }
  if (order.dropi_state === DropiState.SUCCESS) {
    return { label: "Sincronizada Dropi", retryableDropi: false, raw: null };
  }
  if (order.logistic_state === LogisticState.IN_PROGRESS) {
    return { label: "En gestion", retryableDropi: false, raw: null };
  }
  return { label: "Envio pendiente", retryableDropi: false, raw: null };
};

const getVisiblePaginationPages = (currentPage: number, totalPages: number) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }

  const pages = new Set([0, totalPages - 1, currentPage]);
  if (currentPage > 0) pages.add(currentPage - 1);
  if (currentPage < totalPages - 1) pages.add(currentPage + 1);

  const sortedPages = Array.from(pages).sort((a, b) => a - b);
  return sortedPages.reduce<(number | "ellipsis")[]>((items, page, index) => {
    if (index > 0 && page - sortedPages[index - 1] > 1) {
      items.push("ellipsis");
    }
    items.push(page);
    return items;
  }, []);
};

const SIMPLE_EXPORT_FILTERS = new Set(["paid", "pending", "unfulfilled", "fulfilled"]);
const WHATSAPP_EXPORT_FILTERS = new Set(["ws_confirmed", "ws_not_confirmed", "ws_awaiting", "ws_none"]);
const NOT_CONFIRMED_EXPORT_STATUSES = new Set(["rejected", "expired", "canceled", "send_failed"]);
const AWAITING_EXPORT_STATUSES = new Set(["awaiting_response", "pending"]);
const EXPORT_PAGE_SIZE = 100;

const orderMatchesConfirmationFilter = (
  order: Order,
  statuses: Record<string, ConfirmationInfo>,
  activeFilter: string
) => {
  const status = statuses[String(order.id)]?.status;

  if (activeFilter === "ws_confirmed") return status === "confirmed";
  if (activeFilter === "ws_not_confirmed") return Boolean(status && NOT_CONFIRMED_EXPORT_STATUSES.has(status));
  if (activeFilter === "ws_awaiting") return Boolean(status && AWAITING_EXPORT_STATUSES.has(status));
  if (activeFilter === "ws_none") return !status;
  return true;
};

const OrdersTableSkeleton = () => (
  <>
    {Array.from({ length: 7 }).map((_, rowIndex) => (
      <tr key={`orders-skeleton-${rowIndex}`} className="border-b border-gray-100">
        {Array.from({ length: 9 }).map((__, cellIndex) => (
          <td key={cellIndex} className="h-[60px] overflow-hidden px-4 py-3 align-middle">
            <div className={`${cellIndex === 8 ? "ml-auto" : ""} h-2.5 w-16 max-w-full animate-pulse rounded-full bg-gray-100/80`} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const OrderRow = ({
  order,
  fallbackCurrencySymbol,
  language,
  timezone,
  paymentProviderLabels,
  onOpenDetails,
  onRefreshOrders,
  confirmationStatus,
  compact = false,
  selected = false,
}: OrderRowProps) => {
  const [dropiObservedStatus, setDropiObservedStatus] = useState(
    order.dropi_order_status || order.dropi_observed_status || null
  );
  const [isRetryingDropi, setIsRetryingDropi] = useState(false);
  const [isRetryingShopify, setIsRetryingShopify] = useState(false);
  const [isRetryingMastershop, setIsRetryingMastershop] = useState(false);
  const hasDropiError = order.dropi_integration_error;
  const hasShopifyError = order.shopify_integration_error;
  const hasMastershopError = order.mastershop_integration_error;
  const orderCurrencyKey = getOrderDisplayCurrencyCode(order);
  const currencySymbol = orderCurrencyKey ? getCurrencySymbol(orderCurrencyKey) : fallbackCurrencySymbol;
  const displayTotal = getOrderDisplayTotalWithDiscount(order);
  const isDraftOrder = order.order_state === "draft";
  const canceledOrder = isCanceledOrder(order);
  const hasIntegrationError = !isDraftOrder && !canceledOrder && (hasDropiError || hasShopifyError || hasMastershopError);
  const requiresDropiConfirmationRetry =
    !isDraftOrder &&
    !canceledOrder &&
    !hasIntegrationError &&
    confirmationStatus === "confirmed" &&
    isPendingDropiConfirmation(dropiObservedStatus);
  const requiresShopifyManualRetry = !isDraftOrder && !canceledOrder && isShopifyRetryable(order);
  const requiresMastershopManualRetry =
    !isDraftOrder &&
    !canceledOrder &&
    (Boolean(hasMastershopError) ||
      order.mastershop_state === MastershopState.ERROR);
  const logisticsDisplay = getOrderLogisticsDisplay(order, dropiObservedStatus);
  const productSummary = getOrderProductSummary(order);
  const productTitle = order.product_names || productSummary;
  const channelLabel = getOrderChannelLabel(order);
  const cellPadding = compact ? "px-2.5 py-3" : "px-4 py-3.5";
  const dateTimeParts = getOrderDateTimeParts(order.created_at, timezone);
  const customerName = `${order.customer_name || ""} ${order.customer_las_name || ""}`.trim();
  const customerContact = `${order.customer_prefix_phone || ""} ${order.customer_phone || ""}`.trim() || order.customer_email;
  const customerTooltip = [customerName, customerContact].filter(Boolean).join(" · ");
  const chipClass = "block max-w-full";

  const integrationErrorDescription = (() => {
    if (hasMastershopError) return dictionary["sync-error-mastershop-title"]?.[language] || "Error al sincronizar orden con Mastershop";
    if (hasDropiError && hasShopifyError) return dictionary["sync-error-both-title"]?.[language] || "Error al sincronizar orden con Shopify y Dropi";
    if (hasShopifyError) return dictionary["sync-error-shopify-title"]?.[language] || "Error al sincronizar orden con Shopify";
    return dictionary["sync-error-dropi-title"]?.[language] || "Error al sincronizar orden con Dropi";
  })();

  const paymentMethodLabel = (() => {
    const providerLabel = order.payment_provider
      ? paymentProviderLabels[order.payment_provider]
      : null;

    if (order.payment_method === "online") {
      return providerLabel || "Pago en línea";
    }

    if (order.payment_method === "cod" || !order.payment_method) {
      return dictionary["counter-delivery"][language];
    }

    return providerLabel || order.payment_method;
  })();

  const handleRetryDropiConfirmation = async () => {
    if (isRetryingDropi) return;
    setIsRetryingDropi(true);
    const { res, error } = await handleApi(confirmDropiAfterWhatsApp(order.id), false);
    setIsRetryingDropi(false);

    if (error) {
      toast.error("No se pudo confirmar la orden en Dropi. Inténtalo de nuevo.");
      return;
    }

    const nextStatus =
      res?.objects?.[0]?.status ||
      res?.objects?.status ||
      res?.dropiStatusObserved ||
      "PENDIENTE";
    setDropiObservedStatus(nextStatus);
    toast.success("Orden confirmada en Dropi.");
  };

  const handleRetryDropiManualSync = async () => {
    if (isRetryingDropi) return;
    setIsRetryingDropi(true);
    const { error } = await handleApi(retryDropiOrder(order.id), false);
    setIsRetryingDropi(false);

    if (error) {
      toast.error(getErrorMessage(error));
      return;
    }

    toast.success("Orden enviada nuevamente a Dropi.");
    onRefreshOrders?.();
  };

  const handleRetryShopifyManualSync = async () => {
    if (isRetryingShopify) return;
    setIsRetryingShopify(true);
    const { error } = await handleApi(retryShopifyOrder(order.id), false);
    setIsRetryingShopify(false);

    if (error) {
      toast.error(getErrorMessage(error));
      return;
    }

    toast.success("Orden enviada nuevamente a Shopify.");
    onRefreshOrders?.();
  };

  const handleRetryMastershopManualSync = async () => {
    if (isRetryingMastershop) return;
    setIsRetryingMastershop(true);
    const { error } = await handleApi(retryMastershopOrder(order.id), false);
    setIsRetryingMastershop(false);

    if (error) {
      toast.error(getErrorMessage(error));
      return;
    }

    toast.success(dictionary["mastershop-retry-order-sent"][language]);
    onRefreshOrders?.();
  };

  return (
    <tr
      key={order.id}
      className={`cursor-pointer transition-colors ${
        selected ? "bg-blue-50/70 hover:bg-blue-50" : "hover:bg-gray-50"
      }`}
      onClick={() => onOpenDetails(order)}
    >
      <td className={`${cellPadding} overflow-hidden text-xs leading-4 text-gray-500`} title={dateTimeParts.full}>
        <div className="space-y-0.5">
          <div className="truncate font-medium text-gray-600">{dateTimeParts.date}</div>
          <div className="truncate text-[11px] text-gray-400">{dateTimeParts.time}</div>
        </div>
      </td>
      <td
        className={`${cellPadding} overflow-hidden text-sm`}
        title={customerTooltip}
      >
        <div className="truncate font-semibold text-gray-900" title={customerName || customerContact}>
          {customerName}
        </div>
        <div
          className={`mt-1 truncate text-[11px] leading-4 text-gray-400 ${compact ? "hidden" : ""}`}
          title={customerContact}
        >
          {customerContact}
        </div>
      </td>
      <td className={`${cellPadding} overflow-hidden text-sm text-gray-900`} title={productTitle}>
        <div className="truncate font-semibold" title={productTitle}>{productSummary}</div>
        {!compact && Number(order.products_count || 0) > 1 && (
          <div className="mt-1 truncate text-[11px] leading-4 text-gray-400" title={`${order.products_count} productos`}>
            {order.products_count} productos
          </div>
        )}
      </td>
      <td className={`${cellPadding} overflow-hidden text-sm text-gray-900`} title={channelLabel}>
        <div className="space-y-1">
          <NeutralChip title={channelLabel}>{channelLabel}</NeutralChip>
          <div
            className={`${compact ? "hidden" : ""}`}
            onClick={(event) => event.stopPropagation()}
          >
            <OrderFunnelLink order={order} />
          </div>
        </div>
      </td>
      <td
        className={`${cellPadding} overflow-hidden text-sm text-gray-900 whitespace-nowrap`}
        title={formatCurrency(displayTotal, currencySymbol)}
      >
        {formatCurrency(displayTotal, currencySymbol)}
      </td>
      <td className={`${cellPadding} overflow-hidden text-sm text-gray-900`}>
        <span className={chipClass}>
          <NeutralChip
            title={
              order.payment_state === PaidState.PAID
                ? dictionary["paid-state"][language]
                : "Por recaudar"
            }
          >
            {order.payment_state === PaidState.PAID
              ? dictionary["paid-state"][language]
              : "Por recaudar"}
          </NeutralChip>
        </span>
      </td>
      <td className={`${cellPadding} overflow-hidden text-sm text-gray-900`}>
        <span className={chipClass}>
          <NeutralChip title={paymentMethodLabel}>{paymentMethodLabel}</NeutralChip>
        </span>
      </td>
      <td className={`${cellPadding} overflow-hidden text-sm text-gray-900`}>
        <div className="flex min-w-0 items-center gap-2">
          <NeutralChip title={logisticsDisplay.raw || undefined}>{logisticsDisplay.label}</NeutralChip>
          {logisticsDisplay.retryableDropi && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleRetryDropiManualSync();
              }}
              disabled={isRetryingDropi}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-orange-200 text-orange-700 transition-colors hover:bg-orange-300 disabled:opacity-60"
              aria-label="Sincronizar con Dropi"
              title="Sincronizar con Dropi"
            >
              {isRetryingDropi ? (
                <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
            </button>
          )}
          {!logisticsDisplay.retryableDropi && (requiresDropiConfirmationRetry || requiresShopifyManualRetry || requiresMastershopManualRetry || hasIntegrationError) && (
            <div className="flex items-center gap-1">
              {requiresDropiConfirmationRetry && (
                <DropiConfirmationRetry
                  loading={isRetryingDropi}
                  onRetry={handleRetryDropiConfirmation}
                />
              )}
              {requiresShopifyManualRetry && (
                <ShopifyManualRetryButton
                  loading={isRetryingShopify}
                  onRetry={handleRetryShopifyManualSync}
                />
              )}
              {requiresMastershopManualRetry && (
                <MastershopManualRetryButton
                  loading={isRetryingMastershop}
                  onRetry={handleRetryMastershopManualSync}
                  language={language}
                />
              )}
              {hasIntegrationError && (
                <IntegrationErrorIcon
                  title={dictionary["sync-error-title"]?.[language] || "Error de sincronización"}
                  description={integrationErrorDescription}
                />
              )}
            </div>
          )}
        </div>
      </td>
      <td className={`${compact ? "px-2" : "px-4"} py-3 text-right text-sm text-gray-500`}>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenDetails(order);
          }}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-950"
          aria-label={dictionary["view-details"]?.[language] || "Ver detalles"}
          title={dictionary["view-details"]?.[language] || "Ver detalles"}
        >
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </td>
  </tr>
  );
};

const SalesTable = ({
  filterOrders,
  orders,
  activeFilter,
  isLoading = false,
  onOrderDeleted,
  onFilterByDate,
  activeDateFilter,
  onRemoveDateFilter,
  products,
  onFilterByProductId,
  activeProductFilters,
  onRemoveProductFilter,
  onFilterByFunnel,
  activeFunnelFilters,
  onRemoveFunnelFilter,
  confirmationStatuses,
  pagination,
  onPageChange,
  activeCountryFilter,
  countryOptions = [],
  onFilterByCountryCode,
  onRefreshOrders,
  activeSearch,
  onSearch,
  activeSort,
  onSort,
}: SalesTableProps) => {
  const { language, country, timezone } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const fallbackCurrencySymbol = getCurrencySymbol(activeCountryFilter || country);
  const countries = getCountriesList(language);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isDownloadingOrders, setIsDownloadingOrders] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const [isRetryingShopifyFailed, setIsRetryingShopifyFailed] = useState(false);
  const [deleteOrderMessage, setDeleteOrderMessage] = useState(
    dictionary["are-you-sure-you-want-to-delete-this-order"][language]
  );
  const hasCompletedInitialLoadRef = useRef(false);
  const selectedOrderHash = searchParams.get("order");
  const isDetailOpen = Boolean(selectedOrderHash);
  const hasRows = Boolean(orders?.length);
  const showInitialSkeleton = isLoading && !hasCompletedInitialLoadRef.current && !hasRows;
  const showTableBusy = isLoading && hasCompletedInitialLoadRef.current;
  const filterShellClass =
    "flex h-11 w-full items-center gap-2 rounded-xl bg-white px-4 text-sm font-medium text-gray-700 shadow-sm outline-none transition focus-within:ring-2 focus-within:ring-fluxi/15";
  const filterIconClass = "h-4 w-4 shrink-0 text-gray-400";

  // Buscador por nombre/teléfono (debounced -> búsqueda server-side vía URL).
  const [searchInput, setSearchInput] = useState(activeSearch ?? "");

  useEffect(() => {
    const handler = setTimeout(() => {
      if ((activeSearch ?? "") !== searchInput) {
        onSearch?.(searchInput);
      }
    }, 350);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(activeSearch ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSearch]);

  useEffect(() => {
    if (!isLoading) {
      hasCompletedInitialLoadRef.current = true;
    }
  }, [isLoading]);

  // Orden por columna: primer clic = mayor a menor (desc), segundo = menor a mayor (asc).
  const handleSort = (field: "total" | "date") => {
    if (!onSort) return;
    const desc = `${field}_desc`;
    const asc = `${field}_asc`;
    onSort(activeSort === desc ? asc : desc);
  };

  const openOrderDetails = (order: Order) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("order", order.hash_id);
    setSearchParams(nextParams);
  };

  const closeOrderDetails = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("order");
    setSearchParams(nextParams);
  };

  const hasActiveFilters =
    Boolean(activeDateFilter) ||
    activeProductFilters.length > 0 ||
    activeFunnelFilters.length > 0 ||
    Boolean(activeCountryFilter) ||
    activeFilter !== "all";

  const paymentProviderLabels = {
    [PaymentProvider.CONTRA_ENTREGA]: "Contra entrega",
    [PaymentProvider.MERCADO_PAGO]: "Mercado Pago",
    [PaymentProvider.STRIPE]: "Stripe",
    [PaymentProvider.CONFIO]: "Confío",
    [PaymentProvider.SHOPIFY]: "Shopify",
  };

  const activeCountryLabel = activeCountryFilter
    ? countries.find((item) => item.country === activeCountryFilter)?.name || activeCountryFilter
    : null;

  const shopifyFailedCount = orders?.filter(isShopifyRetryable).length ?? 0;

  const openExport = (exportResponse: { s3_url?: string }) => {
    if (exportResponse?.s3_url) {
      window.open(exportResponse.s3_url, "_blank");
    }
  };

  const getBaseExportFilters = () => {
    const productIds = searchParams.get("product_ids")?.split(",").filter(Boolean) || [];

    return {
      date_init: searchParams.get("date_init") || null,
      date_end: searchParams.get("date_end") || null,
      product_ids: productIds.length > 0 ? productIds : null,
      country_code: activeCountryFilter || searchParams.get("country_code") || null,
    };
  };

  const fetchAllOrdersForExport = async () => {
    const baseFilters = getBaseExportFilters();
    const firstPage = await getOrdersByUser({
      ...baseFilters,
      page: 0,
      size: EXPORT_PAGE_SIZE,
    });
    const allOrders: Order[] = Array.isArray(firstPage?.orders) ? [...firstPage.orders] : [];
    const totalPages = Number(firstPage?.pagination?.total_pages || 1);

    for (let page = 1; page < totalPages; page += 1) {
      const response = await getOrdersByUser({
        ...baseFilters,
        page,
        size: EXPORT_PAGE_SIZE,
      });
      if (Array.isArray(response?.orders)) {
        allOrders.push(...response.orders);
      }
    }

    return allOrders;
  };

  const fetchConfirmationStatusesForOrders = async (orderIds: number[]) => {
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId || orderIds.length === 0) return {};

    const statuses: Record<string, ConfirmationInfo> = {};
    for (let index = 0; index < orderIds.length; index += EXPORT_PAGE_SIZE) {
      const chunk = orderIds.slice(index, index + EXPORT_PAGE_SIZE);
      Object.assign(statuses, await getConfirmationStatuses(ownerId, chunk));
    }
    return statuses;
  };

  const handleDownloadOrders = async () => {
    if (isDownloadingOrders) return;

    setIsDownloadingOrders(true);
    try {
      if (WHATSAPP_EXPORT_FILTERS.has(activeFilter)) {
        const baseOrders = await fetchAllOrdersForExport();
        const statuses = await fetchConfirmationStatusesForOrders(baseOrders.map((order) => order.id));
        const orderIds = baseOrders
          .filter((order) => orderMatchesConfirmationFilter(order, statuses, activeFilter))
          .map((order) => order.id);

        if (orderIds.length === 0) {
          toast.error(dictionary["no-orders-found"]?.[language] || "No hay órdenes para descargar");
          return;
        }

        openExport(await downloadOrders({ order_ids: orderIds }));
        return;
      }

      const baseFilters = getBaseExportFilters();
      openExport(await downloadOrdersXlsx({
        ...baseFilters,
        order_filter: SIMPLE_EXPORT_FILTERS.has(activeFilter) ? activeFilter : null,
      }));
    } catch {
      toast.error("No se pudieron descargar las órdenes.");
    } finally {
      setIsDownloadingOrders(false);
    }
  };

  const handleRetryFailedShopifyOrders = async () => {
    if (isRetryingShopifyFailed) return;
    setIsRetryingShopifyFailed(true);
    const { res, error } = await handleApi(retryFailedShopifyOrders(25), false);
    setIsRetryingShopifyFailed(false);

    if (error) {
      toast.error("No se pudieron reintentar las órdenes de Shopify.");
      return;
    }

    const accepted = Array.isArray(res?.results)
      ? res.results.filter((result: { accepted?: boolean }) => result.accepted).length
      : Number(res?.dispatched || 0);
    toast.success(`Reintento Shopify encolado: ${accepted} órdenes.`);
    onRefreshOrders?.();
  };

  const handleDeleteOrder = async (order: Order) => {
    setOrderToDelete(order);
    setOpenModalDelete(true);
    setIsLoadingOrderDetails(true);
    const { res, error } = await handleApi(getOrderDetails(order.hash_id));
    if (error) {
      setDeleteOrderMessage(
        dictionary["error-getting-order-details"][language]
      );
      setIsLoadingOrderDetails(false);
      return;
    }

    const hasDropiIntegration = res?.order?.dropi_state;
    const hasShopifyIntegration = res?.order?.shopify_integration_state;
    const hasMastershopIntegration = res?.order?.mastershop_state;

    let message = dictionary["are-you-sure-you-want-to-delete-this-order"][language];

    if (hasDropiIntegration && hasShopifyIntegration && hasMastershopIntegration) {
      message = dictionary["this-order-is-integrated-with-dropi-shopify-and-mastershop"][language];
    } else if (hasDropiIntegration && hasShopifyIntegration) {
      message = dictionary["this-order-is-integrated-with-dropi-and-shopify"][language];
    } else if (hasDropiIntegration && hasMastershopIntegration) {
      message = dictionary["this-order-is-integrated-with-dropi-and-mastershop"][language];
    } else if (hasShopifyIntegration && hasMastershopIntegration) {
      message = dictionary["this-order-is-integrated-with-shopify-and-mastershop"][language];
    } else if (hasDropiIntegration) {
      message = dictionary["this-order-is-integrated-with-dropi"][language];
    } else if (hasShopifyIntegration) {
      message = dictionary["this-order-is-integrated-with-shopify"][language];
    } else if (hasMastershopIntegration) {
      message = dictionary["this-order-is-integrated-with-mastershop"][language];
    }

    setDeleteOrderMessage(message);
    setIsLoadingOrderDetails(false);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    setIsLoadingOrderDetails(true);
    const { error } = await handleApi(deleteOrder(orderToDelete.id));
    if (error) {
      toast.error(dictionary["error-deleting-order"][language]);
      setIsLoadingOrderDetails(false);
      return;
    }
    toast.success(dictionary["order-deleted"][language]);
    onOrderDeleted(orderToDelete.id.toString());
    setIsLoadingOrderDetails(false);
    setOpenModalDelete(false);
  };

  return (
    <>
      <DeleteOrderModal
        isOpen={openModalDelete}
        onClose={() => setOpenModalDelete(false)}
        message={deleteOrderMessage}
        onConfirm={confirmDeleteOrder}
        loading={isLoadingOrderDetails}
        language={language}
      />

      <div className={`${isDetailOpen ? "rounded-l-xl rounded-r-none" : "rounded-xl"} bg-white shadow-sm`}>
      <div className="flex flex-col gap-3 px-4 pt-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative min-w-[260px] flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={dictionary["search-name-or-phone"]?.[language] ?? "Buscar nombre o teléfono"}
              className="h-12 w-full rounded-full border border-gray-200 bg-white pl-11 pr-11 text-sm text-gray-700 outline-none transition focus:border-fluxi focus:ring-2 focus:ring-fluxi/15"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                aria-label="Limpiar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          {shopifyFailedCount > 0 && (
            <button
              type="button"
              onClick={handleRetryFailedShopifyOrders}
              disabled={isRetryingShopifyFailed}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-blue-500 px-4 text-sm font-semibold text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${isRetryingShopifyFailed ? "animate-spin" : ""}`} />
              {isRetryingShopifyFailed ? "Reintentando Shopify" : `Reintentar Shopify (${shopifyFailedCount})`}
            </button>
          )}
            <button
              type="button"
              onClick={() => setFiltersOpen((current) => !current)}
              className="relative inline-flex h-11 items-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
              aria-expanded={filtersOpen}
            >
              <Filter className="h-4 w-4" />
              Filtro
              {hasActiveFilters && (
                <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-blue-400" />
              )}
            </button>
            <button
              type="button"
              onClick={handleDownloadOrders}
              disabled={isDownloadingOrders}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-gray-100 px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Download className={`h-4 w-4 ${isDownloadingOrders ? "animate-pulse" : ""}`} />
              {isDownloadingOrders
                ? dictionary["downloading"][language]
                : dictionary["download"]?.[language] || "Descargar"}
            </button>
          </div>
        </div>
        <AnimatePresence initial={false}>
          {filtersOpen && (
            <motion.div
              key="orders-filters"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="relative z-30 overflow-visible"
            >
              <div className="grid gap-3 rounded-2xl border border-gray-100 bg-gray-50/80 p-3 md:grid-cols-2 xl:grid-cols-5">
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Producto</p>
                  <ProductFilter
                    products={products}
                    onProductSelect={onFilterByProductId}
                  />
                </div>
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Pais</p>
                  <div className={filterShellClass}>
                    <Globe2 className={filterIconClass} />
                    <select
                      value={activeCountryFilter || ""}
                      onChange={(event) => onFilterByCountryCode?.(event.target.value || null)}
                      className="min-w-0 flex-1 appearance-none bg-transparent pr-6 text-sm font-medium text-gray-700 outline-none"
                    >
                      <option value="">Todos los paises</option>
                      {countryOptions.map((countryCode) => {
                        const countryInfo = countries.find((item) => item.country === countryCode);
                        return (
                          <option key={countryCode} value={countryCode}>
                            {countryInfo ? `${countryInfo.flag} ${countryInfo.name}` : countryCode}
                          </option>
                        );
                      })}
                    </select>
                    <ChevronDown className="pointer-events-none h-4 w-4 shrink-0 text-gray-400" />
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Estado</p>
                  <div className={filterShellClass}>
                    <ListFilter className={filterIconClass} />
                    <select
                      value={activeFilter}
                      onChange={(event) => filterOrders(event.target.value as typeof activeFilter)}
                      className="min-w-0 flex-1 appearance-none bg-transparent pr-6 text-sm font-medium text-gray-700 outline-none"
                    >
                      {STATUS_FILTER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none h-4 w-4 shrink-0 text-gray-400" />
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Embudo</p>
                  <FunnelFilter onFunnelSelect={onFilterByFunnel} />
                </div>
                <div className="min-w-0 xl:justify-self-end">
                  <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Fecha</p>
                  <DateFilter onFilterApply={onFilterByDate} />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {hasActiveFilters && (
        <div className="mt-4 flex flex-wrap gap-2 px-4">
          {activeDateFilter && (
            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700">
              <span>{activeDateFilter.label}</span>
              <button
                type="button"
                onClick={onRemoveDateFilter}
                className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700"
                aria-label="Quitar filtro de fecha"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {activeProductFilters.map((product: { id: string; name: string }) => (
            <div
              key={product.id}
              className="inline-flex max-w-[250px] items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700"
            >
              <span className="truncate">{product.name}</span>
              <button
                type="button"
                onClick={() => onRemoveProductFilter(product.id)}
                className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700"
                aria-label="Quitar filtro de producto"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          {activeFunnelFilters.map((funnel: { id: string; name: string }) => (
            <div
              key={funnel.id}
              className="inline-flex max-w-[250px] items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700"
            >
              <span className="truncate">{funnel.name}</span>
              <button
                type="button"
                onClick={() => onRemoveFunnelFilter(funnel.id)}
                className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700"
                aria-label="Quitar filtro de embudo"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          {activeCountryLabel && (
            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700">
              <span>{activeCountryLabel}</span>
              <button
                type="button"
                onClick={() => onFilterByCountryCode?.(null)}
                className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700"
                aria-label="Quitar filtro de pais"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {activeFilter !== "all" && (
            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700">
              <span>{getStatusFilterLabel(activeFilter)}</span>
              <button
                type="button"
                onClick={() => filterOrders("all")}
                className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700"
                aria-label="Quitar filtro de estado"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* tabla */}
      <div className="relative mt-4 overflow-x-auto">
        <table className={`w-full table-fixed ${isDetailOpen ? "min-w-[980px]" : "min-w-[1180px]"}`}>
          <colgroup>
            <col className={isDetailOpen ? "w-[11%]" : "w-[8%]"} />
            <col className={isDetailOpen ? "w-[13%]" : "w-[12%]"} />
            <col className={isDetailOpen ? "w-[20%]" : "w-[24%]"} />
            <col className={isDetailOpen ? "w-[14%]" : "w-[14%]"} />
            <col className={isDetailOpen ? "w-[7%]" : "w-[7%]"} />
            <col className={isDetailOpen ? "w-[8%]" : "w-[8%]"} />
            <col className={isDetailOpen ? "w-[11%]" : "w-[10%]"} />
            <col className={isDetailOpen ? "w-[12%]" : "w-[13%]"} />
            <col className={isDetailOpen ? "w-[4%]" : "w-[4%]"} />
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              <th className={`${isDetailOpen ? "px-2.5" : "px-4"} overflow-hidden py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap`}>
                <button type="button" onClick={() => handleSort("date")} className="inline-flex max-w-full min-w-0 items-center gap-1 uppercase transition-colors hover:text-gray-700" title={dictionary["date-and-time"]?.[language]}>
                  <HeaderText>{dictionary["date-and-time"]?.[language]}</HeaderText>
                  {activeSort === "date_desc" ? <ArrowDown className="h-3.5 w-3.5" /> : activeSort === "date_asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDownUp className="h-3.5 w-3.5 opacity-40" />}
                </button>
              </th>
              <th className={`${isDetailOpen ? "px-2.5" : "px-4"} overflow-hidden py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap`} title={dictionary["customer"]?.[language]}><HeaderText>{dictionary["customer"]?.[language]}</HeaderText></th>
              <th className={`${isDetailOpen ? "px-2.5" : "px-4"} overflow-hidden py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap`} title="Producto"><HeaderText>Producto</HeaderText></th>
              <th className={`${isDetailOpen ? "px-2.5" : "px-4"} overflow-hidden py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap`} title="Canal"><HeaderText>Canal</HeaderText></th>
              <th className={`${isDetailOpen ? "px-2.5" : "px-4"} overflow-hidden py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap`}>
                <button type="button" onClick={() => handleSort("total")} className="inline-flex max-w-full min-w-0 items-center gap-1 uppercase transition-colors hover:text-gray-700" title={dictionary["total"]?.[language]}>
                  <HeaderText>{dictionary["total"]?.[language]}</HeaderText>
                  {activeSort === "total_desc" ? <ArrowDown className="h-3.5 w-3.5" /> : activeSort === "total_asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDownUp className="h-3.5 w-3.5 opacity-40" />}
                </button>
              </th>
              <th className={`${isDetailOpen ? "px-2.5" : "px-4"} overflow-hidden py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap`} title="Pago"><HeaderText>Pago</HeaderText></th>
              <th className={`${isDetailOpen ? "px-2.5" : "px-4"} overflow-hidden py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap`} title={dictionary["payment-method"]?.[language]}><HeaderText>{dictionary["payment-method"]?.[language]}</HeaderText></th>
              <th className={`${isDetailOpen ? "px-2.5" : "px-4"} overflow-hidden py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap`} title="Logistica"><HeaderText>Logistica</HeaderText></th>
              <th className={`${isDetailOpen ? "px-2" : "px-4"} py-3 w-14`}></th>
            </tr>
          </thead>
          <tbody className={`bg-white divide-y divide-gray-200 transition-opacity duration-150 ${showTableBusy ? "pointer-events-none opacity-45" : "opacity-100"}`}>
            {showInitialSkeleton ? (
              <OrdersTableSkeleton />
            ) : orders?.length === 0 ? (
              <EmptyState language={language} />
            ) : (
              orders.map((order) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  fallbackCurrencySymbol={fallbackCurrencySymbol}
                  language={language}
                  timezone={timezone}
                  paymentProviderLabels={paymentProviderLabels}
                  onOpenDetails={openOrderDetails}
                  onRefreshOrders={onRefreshOrders}
                  confirmationStatus={confirmationStatuses?.[String(order.id)]?.status}
                  compact={isDetailOpen}
                  selected={selectedOrderHash === order.hash_id}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {pagination && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-10 py-7 border-t border-gray-100 bg-[#F7F8FC]">
          <div className="text-sm text-gray-500">
            {pagination.total_items === 0
              ? "Mostrando 0 - 0 de 0"
              : `Mostrando ${pagination.page * pagination.size + 1} - ${Math.min(
                  (pagination.page + 1) * pagination.size,
                  pagination.total_items
                )} de ${pagination.total_items}`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 0}
              className="h-10 px-4 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-600 transition-colors hover:border-fluxi hover:text-fluxi disabled:bg-white disabled:text-gray-300 disabled:hover:border-gray-200"
            >
              {dictionary["previous"]?.[language] || "Anterior"}
            </button>
            {getVisiblePaginationPages(pagination.page, pagination.total_pages).map((page, index) =>
              page === "ellipsis" ? (
                <span
                  key={`ellipsis-${index}`}
                  className="h-10 min-w-8 inline-flex items-center justify-center text-sm text-gray-500"
                >
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  type="button"
                  onClick={() => onPageChange(page)}
                  className={`h-10 min-w-10 rounded-md border text-sm font-medium transition-colors ${
                    page === pagination.page
                      ? "border-fluxi bg-fluxi text-white shadow-sm"
                      : "border-gray-200 bg-white text-gray-700 hover:border-fluxi hover:text-fluxi"
                  }`}
                >
                  {page + 1}
                </button>
              )
            )}
            <button
              type="button"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page + 1 >= pagination.total_pages}
              className="h-10 px-4 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:border-fluxi hover:text-fluxi disabled:bg-white disabled:text-gray-300 disabled:hover:border-gray-200"
            >
              {dictionary["next"]?.[language] || "Siguiente"}
            </button>
          </div>
        </div>
      )}
      </div>
      <OrderDetailDrawer
        hashId={selectedOrderHash}
        onClose={closeOrderDetails}
        onDelete={handleDeleteOrder}
      />
    </>
  );
};

export default SalesTable;
